import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
// import { Link } from 'react-router-dom';
import { API_URLS } from '../config/api';
import API_CONFIG from '../config/api';

gsap.registerPlugin(ScrollTrigger);



const UpcomingEvents = ({ isHomepage = false }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const containerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    fetchEvents();
    initAnimations();
    
    // Listen for event updates from EventMaster
      const handleEventUpdate = () => {
    fetchEvents();
  };
    
    window.addEventListener('eventUpdated', handleEventUpdate);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('eventUpdated', handleEventUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsRef]);

  const fetchEvents = async () => {
    try {
      // Fetch all events from database (not just upcoming)
      const response = await axios.get(API_URLS.UPCOMING_FOR_REGISTRATION, {
        timeout: 5000, // 5 second timeout
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Only use real events from database
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Sort events by start date (most recent first for homepage, upcoming first for events page)
        const sortedEvents = response.data.sort((a, b) => {
          const dateA = new Date(a.startDate);
          const dateB = new Date(b.startDate);
          return isHomepage ? dateB - dateA : dateA - dateB; // Reverse for homepage
        });
        
        // Limit to 3 events for homepage
        const limitedEvents = isHomepage ? sortedEvents.slice(0, 3) : sortedEvents;
        setEvents(limitedEvents);
      } else {
        setEvents([]);
      }
    } catch (error) {
      // console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const initAnimations = () => {
    if (cardsRef.current && cardsRef.current.length > 0) {
      gsap.fromTo(cardsRef.current, 
        {
          opacity: 0,
          y: 50,
          scale: 0.9
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleEventClick = (event) => {
    navigate(`/events`);
  };

  const handleViewRules = (event, e) => {
    e.stopPropagation(); // Prevent event card click
    setSelectedEvent(event);
    setShowRulesModal(true);
  };

  const handleCloseRulesModal = () => {
    setShowRulesModal(false);
    setSelectedEvent(null);
  };

  const handleRegisterForEvent = () => {
    // Close the modal first
    setShowRulesModal(false);
    setSelectedEvent(null);
    
    // Navigate to registration page
    navigate('/register');
  };

  const handleSeeMore = () => {
    navigate('/events');
  };

  const handleViewDescription = (event) => {
    setSelectedEvent(event);
    setShowDescriptionModal(true);
  };

  const handleCloseDescriptionModal = () => {
    setShowDescriptionModal(false);
    setSelectedEvent(null);
  };

  return (
    <div ref={containerRef} className="py-16 px-2 bg-[#f5f5f7] dark:bg-[#18181a] border border-gray-200 rounded-lg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {!isHomepage && (
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              All <span className="text-orange-600">Events</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
            <p className="section-subtitle max-w-3xl mx-auto">
              Discover exciting cricket events and tournaments happening in our community
            </p>
          </div>
        )}

        {isHomepage && (
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Upcoming <span className="text-orange-600">Events</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
            <p className="section-subtitle max-w-3xl mx-auto">
              Stay updated with the latest cricket tournaments and events in Kabir Nagar
            </p>
          </div>
        )}

        <div className="max-w-7xl mx-auto">
          {events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 sm:px-6 lg:px-8">
            {events.map((event, index) => (
          <div
            key={event._id}
            ref={el => cardsRef.current[index] = el}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden
                     transform transition-all duration-300 hover:scale-105
                     hover:shadow-lg shadow-md h-full flex flex-col"
                tabIndex={0}
                role="button"
                onClick={() => handleEventClick(event)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEventClick(event);
                  }
                }}
                aria-label={`View details for ${event.eventName}`}
              >
                {/* Image Container */}
                <div className="relative h-48 overflow-hidden">
                  {event.photoUrl ? (
                    <img
                      src={API_CONFIG.getImageUrl(event.photoUrl)}
                      alt={event.eventName}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  onError={(e) => {
                        e.target.src = '/images/heroSection_img.jpg';
                  }}
                />
              ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                      <svg className="w-12 h-12 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    </div>
                  )}
                  
                  {/* Event Status Badge */}
                  <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Upcoming
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col h-full">
                  {/* Top Content - Fixed Height */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="card-title text-gray-900 mb-2 line-clamp-2 min-h-[3rem]">
                      {event.eventName}
                    </h3>
                    
                    <div className="flex items-center text-gray-500 mb-3">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-caption">
                        {formatDate(event.startDate)}
                      </span>
                    </div>
                    
                    <div className="flex items-start text-gray-500 mb-4 min-h-[3rem] text-left">
                      <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-caption text-left">Location: 118, Ward No 2, Kabir Nagar, Raipur</span>
                    </div>
                  </div>

                  {/* Bottom Buttons - Fixed Position */}
                  <div className="mt-auto space-y-3">
                    {/* View Description Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDescription(event);
                      }}
                      className="w-full bg-transparent border border-blue-500 text-blue-600 py-2 px-4 rounded-lg 
                               font-semibold text-sm transition-all duration-200 hover:bg-blue-500 hover:text-white
                               hover:shadow-lg transform hover:scale-105 flex items-center justify-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Description
                    </button>

                    <button 
                      onClick={(e) => handleViewRules(event, e)}
                      className="w-full bg-transparent border border-orange-500 text-orange-600 py-2 px-4 rounded-lg 
                                   font-semibold text-sm transition-all duration-200 hover:bg-orange-500 hover:text-white
                                   hover:shadow-lg transform hover:scale-105 flex items-center justify-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      View Rules
                    </button>
                  </div>
                </div>
          </div>
            ))}
            </div>
          ) : (
            <div className="text-center py-12">
            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl p-12 max-w-2xl mx-auto">
              <svg className="w-16 h-16 mx-auto mb-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
                <h3 className="text-h4 text-gray-900 mb-4">No Events Available</h3>
                <p className="text-body text-gray-600 mb-6">
                  {isHomepage 
                    ? "Check back soon for upcoming cricket tournaments and events!"
                    : "No events are currently scheduled. New events will appear here when they're added."
                  }
                </p>
                {!isHomepage && (
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg 
                             font-semibold text-base transition-all duration-200 hover:from-blue-700 hover:to-blue-800
                             hover:shadow-lg transform hover:scale-105"
                  >
                    Refresh Events
                  </button>
                )}
            </div>
          </div>
          )}
        </div>
        
        {/* See More Button for Homepage */}
        {isHomepage && events.length > 0 && (
          <div className="text-center mt-8">
            <button
              onClick={handleSeeMore}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-lg 
                       font-semibold text-base transition-all duration-200 hover:from-blue-700 hover:to-blue-800
                       hover:shadow-lg transform hover:scale-105"
            >
              See More
            </button>
          </div>
        )}
      </div>

      {/* Rules Modal */}
      {showRulesModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative max-w-2xl w-full max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">{selectedEvent.eventName}</h3>
                  <p className="text-orange-100 mt-1">
                    {formatDate(selectedEvent.startDate)}
                  </p>
                </div>
                <button
                  onClick={handleCloseRulesModal}
                  className="text-white hover:text-orange-200 p-2 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-4 text-left">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Event Rules & Guidelines
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {selectedEvent.rules || selectedEvent.description ? (
                      <div className="prose prose-sm max-w-none">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {selectedEvent.rules || selectedEvent.description}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-gray-500">No specific rules have been provided for this event.</p>
                        <p className="text-sm text-gray-400 mt-2">Please contact the organizers for more details.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Event Location
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      {selectedEvent.location || "118, Ward No 2, Kabir Nagar, Raipur"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-center">
              <button
                onClick={handleRegisterForEvent}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium transition-colors"
              >
                Register for Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {showDescriptionModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative max-w-2xl w-full max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">{selectedEvent.eventName || selectedEvent.title}</h3>
                  <p className="text-blue-100 mt-1">
                    {formatDate(selectedEvent.startDate)}
                  </p>
                </div>
                <button
                  onClick={handleCloseDescriptionModal}
                  className="text-white hover:text-blue-200 p-2 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-4 text-left">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Event Description
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {selectedEvent.description || 'No description available for this event.'}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Event Location
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      {selectedEvent.location || "118- Ward No.2, Kabir Nagar, Raipur"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-center">
              <button
                onClick={handleRegisterForEvent}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-colors"
              >
                Register for Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingEvents; 